import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {
  VisualizationType,
  VisualizationTypeToParameters,
} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import updateVisualization from 'insights/analytics-visualizations/data/updateVisualization';
import {VISUALIZATION_TYPE_SETTERS} from 'insights/analytics-visualizations/data/VisualizationTypeSetters';
import VisualizationTypeSelector from 'insights/analytics-visualizations/VisualizationTypeSelector';
import {PANE_MARGIN} from 'insights/InsightsConstants';
import {VisualizationConfig} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import {TabContainerHeaderDivider} from 'ui-library/TabContainer';

import BarChartConfigEditor from './BarChartConfigEditor';
import HeatmapConfigEditor from './HeatmapConfigEditor';
import LineChartConfigEditor from './LineChartConfigEditor';
import PieChartConfigEditor from './PieChartConfigEditor';
import ScatterPlotConfigEditor from './ScatterPlotConfigEditor';
import usePreviousVisualizationParametersByType from './usePreviousVisualizationParametersByType';
import {VisualizationConfigEditorComponentProps} from './VisualizationConfigEditorTypes';

type AnalyticsVisualizationParameters = AnalyticsVisualization['parameters'];

// This is used to bootstrap a visualization config if the editor is initialized without a chart type. It
// should not be used in any case where the id is actually being persisted.
export const MINIMAL_CONFIG: VisualizationConfig = {
  __typename: 'AnalyticsVisualizationNode',
  id: 'stub_id',
  parameters: {
    __typename: 'AnalyticsVisualizationTableParametersNode',
    displayColumnsJson: null,
  },
};

export const SETTING_EDITORS: {
  [k in VisualizationType]: React.FC<
    VisualizationConfigEditorComponentProps<VisualizationTypeToParameters[k]>
  > | null;
} = {
  [VisualizationType.BAR_CHART]: BarChartConfigEditor,
  [VisualizationType.HEATMAP]: HeatmapConfigEditor,
  [VisualizationType.LINE_CHART]: LineChartConfigEditor,
  [VisualizationType.PIE_CHART]: PieChartConfigEditor,
  [VisualizationType.SCATTER_PLOT]: ScatterPlotConfigEditor,
  [VisualizationType.TABLE]: null,
};

// Any config editors with tabs need to directly render TabbedConfigEditorSkeleton
export const CONFIG_EDITOR_HAS_TABS: {[k in VisualizationType]: boolean} = {
  [VisualizationType.BAR_CHART]: true,
  [VisualizationType.HEATMAP]: false,
  [VisualizationType.LINE_CHART]: false,
  [VisualizationType.PIE_CHART]: true,
  [VisualizationType.SCATTER_PLOT]: true,
  [VisualizationType.TABLE]: false,
};

export interface VisualizationConfigEditorProps {
  columns: Array<DataFrameColumnDef>;
  onChangeVisualizationConfig: (newVisualization: VisualizationConfig) => void;
  visualizationConfig: VisualizationConfig | null;
  chartTitleOptions?: {
    visualizationName: string;
    onChangeVisualizationName: (newVisualizationName: string) => void;
  };
  useSystemNames: boolean;
  disabledVisualizationTypes?: Array<VisualizationType>;
  isDisabled?: boolean;
  typeSelectorIsDisabled?: boolean;
  isAnalysisTool?: boolean;
  /**
   * Flags to enable the analysis and format tabs.
   *
   * @default true
   * */
  enableAnalysisTab?: boolean;
  enableFormatTab?: boolean;
  enableAggregation?: boolean;
  enableErrorBarColumn?: boolean;
  enableFormatTabForLineChart?: boolean;
}

export default function VisualizationConfigEditor({
  columns,
  onChangeVisualizationConfig,
  visualizationConfig,
  chartTitleOptions,
  useSystemNames,
  disabledVisualizationTypes,
  isDisabled = false,
  typeSelectorIsDisabled = false,
  isAnalysisTool = false,
  enableAnalysisTab = true,
  enableFormatTab = true,
  enableAggregation = true,
  enableErrorBarColumn = false,
  enableFormatTabForLineChart = false,
}: VisualizationConfigEditorProps): JSX.Element | null {
  const previousVisualizationParametersByType = usePreviousVisualizationParametersByType(visualizationConfig);

  /**
   * Update an entire VisualizationConfig
   */
  function handleUpdateVisualizationConfig(update: Partial<VisualizationConfig>): void {
    const newVisualization = updateVisualization(visualizationConfig ?? MINIMAL_CONFIG, update);

    onChangeVisualizationConfig(newVisualization);
  }

  /**
   * Update the parameters part of VisualizationConfig
   */
  function handleUpdateParameters(update: Partial<AnalyticsVisualizationParameters>): void {
    handleUpdateVisualizationConfig({
      parameters: {
        ...(visualizationConfig != null ? visualizationConfig.parameters : {}),
        ...update,
      } as AnalyticsVisualizationParameters,
    });
  }

  /**
   * Update the type of an VisualizationConfig by generating new parameters for the new type
   */
  function handleUpdateType(newVisualizationType: VisualizationType): void {
    const previousVisualizationParameters = previousVisualizationParametersByType[newVisualizationType];

    // If we have saved the previous parameters for this type, then restore those that are not included
    // in the parameters of the current VisualizationType.
    const preservedParameters = {
      ...previousVisualizationParameters,
      ...(visualizationConfig?.parameters ?? MINIMAL_CONFIG.parameters),
    };
    const previousVisualization = {
      ...(visualizationConfig ?? MINIMAL_CONFIG),
      parameters: preservedParameters,
    };

    const setter = new VISUALIZATION_TYPE_SETTERS[newVisualizationType](
      columns,
      previousVisualization,
      useSystemNames,
      enableAggregation,
      enableErrorBarColumn
    );
    handleUpdateVisualizationConfig(setter.getUpdate());
  }

  const visualizationType = visualizationConfig != null ? getVisualizationType(visualizationConfig) : null;
  const VisualizationConfigEditorComponent =
    visualizationType != null ? SETTING_EDITORS[visualizationType] : null;
  const configEditorHasTabs =
    (visualizationType != null && CONFIG_EDITOR_HAS_TABS[visualizationType]) ||
    (enableFormatTabForLineChart && visualizationType === VisualizationType.LINE_CHART);

  return (
    <div className={css({...configEditorStyles.wrapper, overflowY: isAnalysisTool ? undefined : 'auto'})}>
      <div className={css(configEditorStyles.header)}>
        <FieldLabel label={isAnalysisTool ? 'View type' : 'Chart type'} />
        <VisualizationTypeSelector
          isDisabled={typeSelectorIsDisabled}
          visualizationType={visualizationType}
          onChange={handleUpdateType}
          disabledVisualizationTypes={disabledVisualizationTypes}
        />
        {chartTitleOptions != null && (
          <>
            <FieldLabel className="u-small-padding-top" label="View title" />
            <TextInput
              value={chartTitleOptions.visualizationName}
              onChange={(e) => {
                chartTitleOptions.onChangeVisualizationName(e.target.value);
              }}
            />
          </>
        )}
      </div>
      {!configEditorHasTabs && <TabContainerHeaderDivider />}
      {VisualizationConfigEditorComponent != null && (
        <div
          className={
            configEditorHasTabs
              ? css(configEditorStyles.editorWithTabs)
              : classNames(css(configEditorStyles.editorWithoutTabs), 'scrollbar-override')
          }
        >
          <VisualizationConfigEditorComponent
            columns={columns}
            isAnalysisTool={isAnalysisTool}
            onUpdateParameters={handleUpdateParameters}
            // TypeScript doesn't know that the correct AnalyticsVisualizationParameters type matches the
            // VisualizationType that gave us VisualizationConfigEditorComponent.
            // @ts-expect-error
            parameters={visualizationConfig.parameters}
            visualizationConfig={visualizationConfig!}
            useSystemNames={useSystemNames}
            enableAnalysisTab={enableAnalysisTab}
            enableFormatTab={
              enableFormatTab &&
              (visualizationType !== VisualizationType.LINE_CHART || enableFormatTabForLineChart)
            }
            enableAggregation={enableAggregation}
            enableErrorBarColumn={enableErrorBarColumn}
            isDisabled={isDisabled}
          />
        </div>
      )}
    </div>
  );
}

export const configEditorStyles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: PANE_MARGIN,
  },
  editorWithTabs: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  editorWithoutTabs: {
    padding: `0px ${PANE_MARGIN}px ${PANE_MARGIN}px ${PANE_MARGIN}px`,
    overflowY: 'auto',
  },
});
